import { useCallback, useRef } from 'react';
import { Index, MultiGrid } from 'react-virtualized';
import { QueryType } from '../../../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../../../utils/types';
import { MultiGridTable } from '../../../../MultiGridTable';
import { useChartContext } from '../../../ChartContext';
import useRetentionTableHeaderRenderer from '../useRetentionTableHeaderRenderer';
import {
  COLUMN_OFFSET_PER_TYPE,
  DEFAULT_FIXED_ROW_WIDTH,
  DEFAULT_ROW_HEADER_HEIGHT,
} from '../utils';

interface IProps {
  analysisId: string;
}

export default ({ analysisId }: IProps) => {
  const headerGridRef = useRef<MultiGrid>(null);

  const { chartData } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Retention);

  const { table } = chartData;

  const { cellRenderer } = useRetentionTableHeaderRenderer(analysisId);

  const columnOffset = COLUMN_OFFSET_PER_TYPE[table.type];

  const columnWidthRenderer = useCallback(
    ({ index }: Index) => {
      // first column for retention over time is always fixed width
      // first & second column for retention is always fixed width
      if (index < columnOffset) {
        return DEFAULT_FIXED_ROW_WIDTH;
      }

      // width of the multigrid
      const multiGridWidth = headerGridRef.current?.props?.width;

      // the width of the retention header (the non-fixed part)
      // should fill the multigrid width, minus the fixed width
      if (multiGridWidth) {
        return multiGridWidth - columnOffset * DEFAULT_FIXED_ROW_WIDTH;
      }

      return DEFAULT_FIXED_ROW_WIDTH;
    },
    [headerGridRef, columnOffset],
  );

  const onResize = useCallback(() => {
    if (headerGridRef?.current) {
      headerGridRef.current.recomputeGridSize();
    }
  }, [headerGridRef]);

  return (
    <MultiGridTable
      ref={headerGridRef}
      columnCount={columnOffset + 1}
      rowCount={1}
      cellRenderer={cellRenderer}
      rowHeight={DEFAULT_ROW_HEADER_HEIGHT}
      columnWidthRenderer={columnWidthRenderer}
      fixedColumnCount={2}
      onResize={onResize}
    />
  );
};
